import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import { GuestBottomNav } from '../components/GuestBottomNav';
import { BellIcon } from '../components/BellIcon';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { toast } from 'sonner';
import { feedbackPaySend, feedbackPayArrived } from '../utils/appFeedback';
import { useAutoCancelDraft } from '../hooks/useAutoCancelDraft';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Info icon SVG (blue) — clickable to reveal a tip explainer
const InfoIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
    <path d="M12 18.75V12M12 7.5V5.25M23.25 12C23.25 18.2132 18.2132 23.25 12 23.25C5.7868 23.25 0.75 18.2132 0.75 12C0.75 5.7868 5.7868 0.75 12 0.75C18.2132 0.75 23.25 5.7868 23.25 12Z" stroke="var(--accent-purple)" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export default function GuestPaymentPage() {
  const { txnId } = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [selectedTip, setSelectedTip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [customTip, setCustomTip] = useState('');
  const [tipInfoOpen, setTipInfoOpen] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // If the guest leaves this screen (back, bottom-nav, app close) without
  // paying, auto-cancel the draft so it doesn't linger as pending.
  useAutoCancelDraft({ txnId, status: transaction?.status, kind: 'real' });

  const fetchTransaction = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transactions/${txnId}`, { headers });
      setTransaction(res.data);
      if (res.data.status === 'PAID') setPaymentSuccess(true);
      if (res.data.status === 'EXPIRED' || res.data.status === 'CANCELLED') {
        // Silent recovery — just send the guest back to the QR screen.
        // A payment that timed out or was cancelled by the venue is not
        // an error state worth alerting the guest about.
        navigate('/guest/qr', { replace: true });
      }
    } catch (err) {
      // Any error (incl. 404 if the row was wiped) → silently go home.
      navigate('/guest/qr', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [txnId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTransaction();
    // Poll so the guest page reacts in real-time to venue-side cancel or
    // the 60s auto-expiry — otherwise the guest could still tap Pay on a
    // stale card and hit a 410 at the end.
    const iv = window.setInterval(fetchTransaction, 2000);
    return () => window.clearInterval(iv);
  }, [fetchTransaction]);

  const tipOptions = [8, 15, 25, 50];

  if (loading || !transaction) {
    return (
      <div className="app-shell" style={{ backgroundColor: 'var(--surface)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src="/allure-logo.png" alt="" style={{ width: 80, height: 71, objectFit: 'contain' }} className="animate-pulse" />
        <GuestBottomNav />
      </div>
    );
  }

  // ========== Payment Success ==========
  if (paymentSuccess) {
    return (
      <div className="app-shell" style={{ backgroundColor: 'var(--surface)' }}>
        <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'flex-end' }}>
          <button aria-label="Notifications" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <BellIcon />
          </button>
        </div>
        <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0 }}>
          <RouteTransitionLogo width={140} height={125} dataTestId="guest-payment-success-shared-logo" />

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 20 }}
          >
            <div style={{ width: 70, height: 70, borderRadius: '50%', backgroundColor: 'var(--status-success-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
              <Check size={36} color="white" strokeWidth={3} />
            </div>
            <h2 style={{ fontFamily: "'Inria Serif', serif", fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 20 }}>Payment Successful!</h2>

            <div style={{ width: '100%', maxWidth: 300, background: 'var(--surface-2)', borderRadius: 16, padding: 20 }}>
              {[['Original bill', `${transaction.original_amount.toFixed(2)}€`, 'var(--text)'],
                [`Allure discount (${Math.round(transaction.discount_percent)}%)`, `− ${(transaction.original_amount - transaction.discounted_amount).toFixed(2)}€`, 'var(--status-success-text)'],
                ['Service fee', `+ ${Number(transaction.guest_flat_fee || 0).toFixed(2)}€`, 'var(--text)'],
                ...(transaction.tip_amount > 0 ? [[`Staff tip (${transaction.tip_percent}%)`, `+ ${transaction.tip_amount.toFixed(2)}€`, 'var(--status-success-text)']] : []),
              ].map(([label, val, valColor]) => (
                <div key={`payment-summary-${label}`} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text-2)' }}>{label}</span>
                  <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: valColor }}>{val}</span>
                </div>
              ))}
              <div style={{ borderTop: '1px solid var(--card-border)', paddingTop: 10, marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>You paid</span>
                <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{transaction.total_customer_pays.toFixed(2)}€</span>
              </div>
              <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-2)', textAlign: 'center', marginTop: 10 }}>Ref: {transaction.payment_reference}</p>
            </div>

            <button
              onClick={() => navigate('/guest/home')}
              className="premium-pill-button"
              style={{
                marginTop: 24, padding: '13px 40px', fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, cursor: 'pointer',
              }}
            >Back to Home</button>
          </motion.div>
        </div>
        <GuestBottomNav />
      </div>
    );
  }

  // ========== Payment Pending ==========
  return (
    <div className="app-shell" style={{ backgroundColor: 'var(--surface)' }}>
      {/* Bell */}
      <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'flex-end' }}>
        <button aria-label="Notifications" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <BellIcon />
        </button>
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0 }}>
        {/* Logo */}
        <RouteTransitionLogo width={140} height={125} dataTestId="guest-payment-shared-logo" />

        {/* Price: 100.00€ → 90.00€ */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 40, marginBottom: 10 }}>
          <span
            data-testid="guest-payment-original-price"
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 28,
              fontWeight: 700,
              color: 'var(--text)',
              textDecoration: 'line-through',
            }}
          >
            {transaction.original_amount.toFixed(2)}€
          </span>
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 24, color: 'var(--text)' }}>→</span>
          <span
            data-testid="guest-payment-discounted-price"
            style={{
              fontFamily: "'Inria Serif', serif",
              fontSize: 32,
              fontWeight: 700,
              color: 'var(--status-success-text)',
              textDecoration: 'underline',
              textUnderlineOffset: '4px',
              textDecorationColor: 'var(--status-success-text)',
            }}
          >
            {(Number(transaction.discounted_amount || 0) + Number(transaction.guest_flat_fee || 0.39)).toFixed(2)}€
          </span>
        </div>

        {/* Fee breakdown line */}
        <p
          data-testid="guest-payment-fee-breakdown"
          style={{
            fontFamily: "'Inria Serif', serif",
            fontSize: 13,
            color: 'var(--text-2)',
            textAlign: 'center',
            margin: '0 0 28px',
            lineHeight: 1.45,
            maxWidth: 280,
          }}
        >
          {Number(transaction.discount_percent || 0) > 0
            ? `${Number(transaction.discounted_amount || 0).toFixed(2)}€ after ${Math.round(transaction.discount_percent)}% discount + ${Number(transaction.guest_flat_fee || 0.39).toFixed(2)}€ service fee`
            : `Includes ${Number(transaction.guest_flat_fee || 0.39).toFixed(2)}€ service fee`}
        </p>

        {/* Optional tip + info icon */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, position: 'relative' }}>
          <span style={{
            fontFamily: "'Inria Serif', serif",
            fontSize: 22,
            fontWeight: 400,
            color: 'var(--text)',
          }}>
            Optional staff tip
          </span>
          <button
            type="button"
            onClick={() => setTipInfoOpen((v) => !v)}
            aria-label="What is the staff tip?"
            aria-expanded={tipInfoOpen}
            data-testid="guest-tip-info-button"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 4,
              cursor: 'pointer',
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <InfoIcon />
          </button>
        </div>

        {/* Tip explainer popover */}
        <AnimatePresence initial={false}>
          {tipInfoOpen && (
            <motion.div
              key="tip-info"
              initial={{ opacity: 0, y: -6, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -6, height: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              style={{ overflow: 'hidden', width: '100%', maxWidth: 320, marginBottom: 12 }}
              data-testid="guest-tip-info-popover"
            >
              <div style={{
                background: 'var(--accent-surface, #F4F1FB)',
                border: '1px solid var(--accent-border-soft, #E1D8F1)',
                borderRadius: 14,
                padding: '12px 14px',
                color: 'var(--accent-text-soft)',
                fontSize: 13,
                lineHeight: 1.45,
              }}>
                Tipping is completely optional. Whatever you choose to tip is added on top of your bill and goes 100% to the venue&apos;s staff who served you — Allure does not take any cut.
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tip percentage buttons */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          {tipOptions.map((tip) => {
            const isSelected = selectedTip === tip;
            return (
              <button
                key={tip}
                onClick={() => {
                  if (isSelected) {
                    setSelectedTip(0);
                    setCustomTip('');
                  } else {
                    setSelectedTip(tip);
                    setCustomTip((transaction.discounted_amount * tip / 100).toFixed(2));
                  }
                }}
                data-testid={`guest-tip-${tip}-button`}
                aria-pressed={isSelected}
                style={{
                  padding: '8px 14px',
                  borderRadius: 10,
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 18,
                  fontWeight: 400,
                  color: isSelected ? 'var(--accent-text-soft)' : 'var(--text-3)',
                  background: isSelected ? 'var(--accent-surface)' : 'var(--surface-2)',
                  border: isSelected ? '1px solid var(--accent-border-soft)' : '1px solid var(--card-border)',
                  cursor: 'pointer',
                  transition: 'all 150ms ease',
                }}
              >
                {tip}%
              </button>
            );
          })}
        </div>

        {/* Editable tip amount box */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
          <input
            type="number"
            inputMode="decimal"
            value={customTip}
            onChange={(e) => {
              setCustomTip(e.target.value);
              setSelectedTip(0);
            }}
            placeholder="0.00"
            data-testid="guest-tip-amount-input"
            style={{
              width: 120, height: 40,
              padding: '0 12px',
              fontFamily: "'Inria Serif', serif", fontSize: 20,
              color: 'var(--text)', textAlign: 'center',
              backgroundColor: 'var(--surface-2)', border: '1px solid var(--card-border)',
              borderRadius: 8, outline: 'none',
            }}
          />
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, color: 'var(--text-2)' }}>€</span>
        </div>

        {/* Pay button - green pill, smaller */}
        <motion.button
          onClick={() => {
            // Use custom tip amount if set
            const tipPct = customTip !== '' 
              ? (transaction.discounted_amount > 0 ? (parseFloat(customTip) || 0) / transaction.discounted_amount * 100 : 0)
              : selectedTip;
            setPaying(true);
            // Rising "sending" whoosh + press vibration as the guest fires off the payment.
            feedbackPaySend();
            axios.put(
              `${API}/transactions/${txnId}/pay`,
              { tip_percent: tipPct },
              { headers }
            ).then(res => {
              setTransaction(res.data);
              setPaymentSuccess(true);
              // Guest-side "arrived" confirmation once the server confirms.
              feedbackPayArrived();
              toast.success('Payment successful!');
            }).catch(err => {
              const status = err.response?.status;
              const detail = err.response?.data?.detail || 'Payment failed';
              if (status === 410) {
                // Payment expired on the server side while the guest was
                // deciding. Silently take them back — no error toast.
                navigate('/guest/qr', { replace: true });
                return;
              }
              if (status === 402) {
                toast.error(detail, {
                  action: {
                    label: 'Add card',
                    onClick: () => navigate('/guest/settings/payment-method'),
                  },
                  duration: 6000,
                });
              } else {
                toast.error(detail);
              }
            }).finally(() => setPaying(false));
          }}
          disabled={paying}
          whileTap={{ scale: 0.98 }}
          data-testid="guest-pay-button"
          style={{
            width: 220,
            padding: '14px 0',
            fontFamily: "'Inria Serif', serif",
            fontSize: 26,
            fontWeight: 400,
            color: '#FFFFFF',
            background: '#1DB954',
            border: 'none',
            borderRadius: 9999,
            cursor: paying ? 'not-allowed' : 'pointer',
            opacity: paying ? 0.6 : 1,
            boxShadow: '0 6px 20px rgba(29,185,84,0.3)',
          }}
        >
          {paying ? 'Processing...' : 'Pay'}
        </motion.button>

        {/* Cancel button */}
        <button
          onClick={() => {
            sessionStorage.setItem('allure.suppressPendingRedirect', txnId);
            navigate('/guest/qr');
          }}
          disabled={paying}
          data-testid="guest-payment-cancel-button"
          style={{
            marginTop: 14,
            padding: '10px 28px',
            fontFamily: "'Inria Serif', serif",
            fontSize: 16,
            fontWeight: 400,
            color: 'var(--text-2)',
            background: 'transparent',
            border: 'none',
            cursor: paying ? 'not-allowed' : 'pointer',
            opacity: paying ? 0.4 : 1,
          }}
        >
          Cancel
        </button>
      </div>

      <GuestBottomNav />
    </div>
  );
}
